// 站点 SEO 元信息管理。
//
// 背景：本站是纯客户端渲染（CSR）的 SPA，构建产物 index.html 里只有一条站点级
// title/description；爬虫拿到的 HTML 里没有任何页面级信息。这里把「写 head」这件事
// 收敛到唯一一个模块，让路由切换、以及详情页数据到达后，都能更新
// title / description / canonical / og / twitter / JSON-LD。
//
// 约定：
//   1. 本模块是 <title> 的唯一写入者（App.vue 的标题赋值也改走 setSeoTitle），
//      否则多处直接写 document.title 会互相覆盖。
//   2. applySeo 是**局部更新**语义：字段为 undefined 表示「保持不变」。
//      这样详情页只更新 description/JSON-LD 时，不会把路由级设置的 canonical 冲掉。
//   3. 传 jsonLd: [] 表示清除上一页的结构化数据（路由切换时必须清，否则会把
//      题目页的 LearningResource 带到别的页面上）。
//   4. 本模块只依赖浏览器 DOM；单元测试环境（jsdom）下同样可用。

import type { ProblemView } from "@/types/problem";
import type { DiscussView } from "@/types/discuss";

/** 站点主域名。canonical / og:url 一律以它为准，避免 www、CDN 域名造成重复内容。 */
export const SITE_ORIGIN = "https://oj.didapipa.com";
export const SITE_NAME = "DidaOJ";

export const DEFAULT_DESCRIPTION =
  "DidaOJ 是一个在线评测系统（Online Judge），提供编程题目练习、比赛、讨论、题单与代码评测等功能，支持多种编程语言。";

export const DEFAULT_KEYWORDS =
  "DidaOJ,在线评测系统,OJ,编程题库,算法竞赛,代码评测,刷题";

/** 可收录页面用的 robots 指令：放开大图预览与摘要长度，提升搜索结果点击率。 */
const ROBOTS_INDEXABLE =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const ROBOTS_NOINDEX = "noindex, nofollow";

export interface SeoInput {
  /** <title> 全文（由调用方决定是否带站点名后缀） */
  title?: string;
  description?: string;
  keywords?: string;
  /** 相对路径或绝对地址；相对路径会补上 SITE_ORIGIN */
  canonical?: string;
  image?: string;
  /** og:type，默认在 index.html 里是 website */
  type?: string;
  noindex?: boolean;
  /** 页面级结构化数据；传 [] 表示清除 */
  jsonLd?: Record<string, unknown>[] | null;
}

/** 把相对路径补成绝对地址；已经是 http(s) 的地址原样返回。 */
export function toAbsoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  return SITE_ORIGIN + (pathOrUrl.startsWith("/") ? pathOrUrl : "/" + pathOrUrl);
}

function ensureMeta(attr: "name" | "property", key: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  // 标记为 SEO 模块管理的节点，便于排查与测试断言
  el.setAttribute("data-seo", "1");
  return el;
}

function setMeta(attr: "name" | "property", key: string, content: string): void {
  ensureMeta(attr, key).setAttribute("content", content);
}

function ensureCanonicalLink(): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("data-seo", "1");
  return el;
}

/** 页面级 JSON-LD 固定在单个 script 节点里，避免切页后越积越多。 */
function setJsonLd(items: Record<string, unknown>[] | null): void {
  const selector = 'script[type="application/ld+json"][data-seo="page-jsonld"]';
  const existing = document.head.querySelector<HTMLScriptElement>(selector);
  if (!items || items.length === 0) {
    if (existing) {
      existing.remove();
    }
    return;
  }
  const el = existing ?? document.createElement("script");
  if (!existing) {
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo", "page-jsonld");
    document.head.appendChild(el);
  }
  // 多条结构化数据合并成一个数组，是 JSON-LD 的合法写法
  el.textContent = JSON.stringify(items.length === 1 ? items[0] : items);
}

/** 应用页面级 SEO 元信息（局部更新，见文件头注释）。 */
export function applySeo(input: SeoInput): void {
  if (typeof document === "undefined") {
    return;
  }

  if (input.title !== undefined) {
    document.title = input.title;
    setMeta("property", "og:title", input.title);
    setMeta("name", "twitter:title", input.title);
  }
  if (input.description !== undefined) {
    setMeta("name", "description", input.description);
    setMeta("property", "og:description", input.description);
    setMeta("name", "twitter:description", input.description);
  }
  if (input.keywords !== undefined) {
    setMeta("name", "keywords", input.keywords);
  }
  if (input.canonical !== undefined) {
    const url = toAbsoluteUrl(input.canonical);
    ensureCanonicalLink().setAttribute("href", url);
    setMeta("property", "og:url", url);
  }
  if (input.image !== undefined) {
    const url = toAbsoluteUrl(input.image);
    setMeta("property", "og:image", url);
    setMeta("name", "twitter:image", url);
  }
  if (input.type !== undefined) {
    setMeta("property", "og:type", input.type);
  }
  if (input.noindex !== undefined) {
    setMeta("name", "robots", input.noindex ? ROBOTS_NOINDEX : ROBOTS_INDEXABLE);
  }
  if (input.jsonLd !== undefined) {
    setJsonLd(input.jsonLd);
  }
}

/** 只更新 <title>（含 og:title / twitter:title）的快捷方式。 */
export function setSeoTitle(title: string): void {
  applySeo({ title });
}

// ---------------------------------------------------------------------------
// 路由级 SEO 文案
// ---------------------------------------------------------------------------

// 路由名 → 页面描述。放在这里而不是 view-config.ts：description 只服务于 SEO，
// 没有菜单/侧边栏用途，集中一处更便于统一维护文案长度与关键词。
export const ROUTE_DESCRIPTIONS: Record<string, string> = {
  home: "DidaOJ 在线评测系统首页：题库、比赛、讨论、每日一题与评测统计一览，支持多种编程语言在线提交。",
  "problem-list":
    "DidaOJ 题库：按标签、难度、来源筛选 2000+ 道编程题目，支持 C++、Java、Python 等多语言在线提交与即时评测。",
  "problem-tags": "DidaOJ 题目标签总览：按算法与知识点浏览题目，快速定位想练的方向。",
  "problem-detail":
    "DidaOJ 题目详情：查看题面、输入输出样例、时间与内存限制，并直接在线提交代码评测。",
  "problem-statistics": "DidaOJ 题目提交统计：通过率、提交次数与解题趋势。",
  "problem-daily-list": "DidaOJ 每日一题：每天一道精选题目，坚持打卡提升算法与编程能力。",
  "problem-daily-detail": "DidaOJ 每日一题详情：查看当日题目、题面与题解。",
  "problem-collection-list": "DidaOJ 题单：按知识点与训练目标组织的题目集合，循序渐进的练习路径。",
  "collection-detail": "DidaOJ 题单详情：查看题单内的题目列表、完成进度与排名。",
  "collection-rank": "DidaOJ 题单排名：查看题单完成进度排行。",
  "judge-list": "DidaOJ 评测列表：查看最近的代码提交与评测结果。",
  "judge-detail": "DidaOJ 评测详情：查看提交代码、各测试点结果与运行时间、内存信息。",
  "contest-list": "DidaOJ 比赛列表：查看进行中与历史编程竞赛，支持在线参赛与实时排名。",
  "contest-recent": "DidaOJ 近期比赛：即将开始与最近结束的编程竞赛一览。",
  "contest-detail": "DidaOJ 比赛详情：查看赛制、赛题与实时排行榜。",
  "contest-rank": "DidaOJ 比赛排名：实时榜单与各题解题情况。",
  "contest-statistics": "DidaOJ 比赛统计：各题通过率与提交分布。",
  "discuss-list": "DidaOJ 讨论区：算法题解、学习经验与技术交流。",
  "discuss-list-problem": "DidaOJ 题目讨论：围绕题目的题解、思路与答疑。",
  "discuss-detail": "DidaOJ 讨论详情：查看帖子正文与回复讨论。",
  "rank-ac-all": "DidaOJ AC 排名：按通过题目数排列的用户榜单。",
  "rank-ac-problem": "DidaOJ 题量排名：按解题总数排列的用户榜单。",
  "rank-ac-problem-today": "DidaOJ 今日题量排名：今日解题数排行。",
  "rank-ac-problem-day7": "DidaOJ 7 日题量排名：最近 7 天解题数排行。",
  "rank-ac-problem-day30": "DidaOJ 30 日题量排名：最近 30 天解题数排行。",
  "rank-ac-problem-year": "DidaOJ 1 年题量排名：最近一年解题数排行。",
  "study-view": "DidaOJ 研习：课程方案与算法专题学习路径。",
  "study-agent": "DidaOJ 研习：算法专题与学习智能体。",
  "bot-game-list": "DidaOJ 机器人对战：编写 AI 参与棋类与策略对战。",
  "bot-game": "DidaOJ 机器人对战：查看游戏规则、对局与排行榜。",
  "bot-match-list": "DidaOJ 对局列表：查看机器人之间的历史对局与回放。",
  "bot-match": "DidaOJ 对局详情：查看对局过程与结果。",
  "bot-agent-list": "DidaOJ 选手列表：浏览参与对战的机器人选手。",
  "bot-about": "DidaOJ 对战说明：机器人对战的规则、接入方式与赛制说明。",
  "system-status": "DidaOJ 系统状态：评测机负载、队列与运行情况。",
  "system-about": "关于 DidaOJ：站点介绍、功能说明与联系方式。",
  user: "DidaOJ 用户主页：查看该用户的解题记录、通过数量与排名。",
  // 错误页：两个页面都是 noindex（见 NOINDEX_ROUTE_NAMES），描述只用于兜底与
  // 社交卡片预览；文案里带上错误码，让页面性质一眼可辨。
  "404": "404 页面不存在：你访问的地址没有对应的内容，可能链接已失效或地址输入有误。",
  "403": "403 无访问权限：当前账号没有查看该页面的权限，请更换访问目标或登录后重试。",
};

// 这些路由不该被搜索引擎收录：登录注册、账号设置、私信、错误页，以及
// 「公开但没有收录价值」的页面——个人提交记录（/judge、/judge/:id）与用户主页
// （/user/:username）。后两类同时被 public/robots.txt 挡在抓取之外，sitemap 也不收录，
// 三处口径保持一致；这里再加 noindex 是冗余兜底（被 robots 挡住的页面，爬虫根本读不到
// meta，所以真正的开关是 robots，noindex 只防「有人在 robots 之外直接引用」）。
// 另有两条通用规则（见 isNoindexRoute）：needLogin 的页面、manage-* 管理页。
const NOINDEX_ROUTE_NAMES = new Set([
  "404",
  "403",
  "login",
  "register",
  "user-modify",
  "user-forget",
  "message",
  "user",
  "judge-list",
  "judge-detail",
]);

/**
 * 判断某条路由是否应当 noindex。
 * 兜底策略：路由名未知（null）时按 noindex 处理——宁可少收录，也不要收录一个
 * 内容不确定的页面。
 */
export function isNoindexRoute(name: string | null | undefined, needLogin?: boolean): boolean {
  if (!name) {
    return true;
  }
  if (needLogin) {
    return true;
  }
  if (name.startsWith("manage-")) {
    return true;
  }
  return NOINDEX_ROUTE_NAMES.has(name);
}

// ---------------------------------------------------------------------------
// 正文摘要与结构化数据
// ---------------------------------------------------------------------------

/**
 * 把 markdown 正文压成一段纯文本摘要，用于 meta description。
 *
 * 题面里混着代码块、LaTeX 公式、内联 HTML（本站自定义标签 oj-problem 等）、
 * 表格与短代码，直接截断会得到一串符号，因此逐类剥掉后再按长度截断。
 */
export function plainTextFromMarkdown(markdown: string | undefined, limit = 150): string {
  if (!markdown) {
    return "";
  }

  let text = markdown;
  text = text.replace(/```[\s\S]*?```/g, " "); // 围栏代码块
  text = text.replace(/~~~[\s\S]*?~~~/g, " ");
  text = text.replace(/\$\$[\s\S]*?\$\$/g, " "); // 块级公式
  text = text.replace(/\$[^$\n]*\$/g, " "); // 行内公式
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, " "); // 图片
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1"); // 链接保留可见文字
  text = text.replace(/<[^>]+>/g, " "); // 内联 HTML 与自定义标签
  text = text.replace(/^\s{0,3}#{1,6}\s+/gm, " "); // ATX 标题
  text = text.replace(/^\s{0,3}>\s?/gm, " "); // 引用
  text = text.replace(/^\s*[-*+]\s+/gm, " "); // 无序列表
  text = text.replace(/^\s*\d+[.)]\s+/gm, " "); // 有序列表
  text = text.replace(/^\s*\|.*\|\s*$/gm, " "); // 表格行
  text = text.replace(/[*_`~]/g, ""); // 强调与行内代码标记
  text = text.replace(/\s+/g, " ").trim();

  if (text.length <= limit) {
    return text;
  }
  return text.slice(0, limit).trimEnd() + "…";
}

/** 拼接最终 description 并保证不超过常见搜索结果截断长度。 */
function composeDescription(parts: string[], limit = 158): string {
  const text = parts
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(" ");
  return plainTextFromMarkdown(text, limit);
}

function problemTagNames(problem: ProblemView): string[] {
  return (problem.tags ?? []).map((tag) => tag.name).filter((name) => !!name);
}

function breadcrumb(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: toAbsoluteUrl(item.path),
    })),
  };
}

/** 公开题目详情页的 canonical 路径。 */
export function problemPath(key: string): string {
  return `/problem/${encodeURIComponent(key)}`;
}

/** 每日一题详情页的 canonical 路径。 */
export function dailyProblemPath(dailyId: string): string {
  return `/problem/daily/${encodeURIComponent(dailyId)}`;
}

/**
 * 应用公开题目详情页的 SEO。
 *
 * 题目页是本站最大的可索引内容面（公开题目 2000+），因此这里除了标题，还会用题面
 * 正文生成唯一的 description，并输出 LearningResource + BreadcrumbList 结构化数据。
 *
 * canonicalPath 用于「同一道题挂在别的地址上」的场景（如每日一题
 * /problem/daily/{id}），不传则用题目自身的 /problem/{key}。
 */
export function applyProblemSeo(problem: ProblemView, canonicalPath?: string): void {
  const path = canonicalPath ?? problemPath(problem.key);
  const tags = problemTagNames(problem);
  const summary = plainTextFromMarkdown(problem.description, 110);
  const description = composeDescription([
    problem.difficultyName ? `${problem.title}（${problem.difficultyName}）` : problem.title,
    summary,
    tags.length > 0 ? `标签：${tags.slice(0, 6).join("、")}` : "",
  ]);

  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "LearningResource",
      name: problem.title,
      description,
      url: toAbsoluteUrl(path),
      identifier: problem.key,
      inLanguage: "zh-CN",
      isAccessibleForFree: true,
      learningResourceType: "编程题目",
      ...(problem.difficultyName ? { educationalLevel: problem.difficultyName } : {}),
      ...(tags.length > 0 ? { keywords: tags.join(",") } : {}),
    },
    breadcrumb([
      { name: SITE_NAME, path: "/" },
      { name: "题库", path: "/problem" },
      { name: problem.title, path },
    ]),
  ];

  applySeo({
    description,
    canonical: path,
    type: "article",
    // 私有题目不进搜索引擎
    noindex: problem.private === true,
    jsonLd,
  });
}

/** 每日一题：题目本体走 applyProblemSeo，只是 canonical 换成每日一题的地址。 */
export function applyDailyProblemSeo(dailyId: string, problem: ProblemView): void {
  applyProblemSeo(problem, dailyProblemPath(dailyId));
}

/**
 * 比赛题目：一律 noindex。
 *
 * 比赛期间题面属于「按赛制不该公开」的内容，比赛结束后同一道题也会落到
 * /problem/{key} 这个正式地址上——两个地址都收录就是重复内容，因此这里只
 * 固定 canonical、不做收录。
 */
export function applyContestProblemSeo(contestId: number, problemIndex: number): void {
  applySeo({
    canonical: `/contest/${contestId}/problem/${problemIndex}`,
    noindex: true,
    jsonLd: [],
  });
}

/** 讨论详情页的 canonical 路径：比赛内讨论与站内讨论是两个地址。 */
export function discussPath(discuss: DiscussView): string {
  if (discuss.contestId > 0) {
    return `/contest/${discuss.contestId}/discuss/${discuss.id}`;
  }
  return `/discuss/${discuss.id}`;
}

/**
 * 应用讨论详情页的 SEO。
 * 比赛内的讨论同样 noindex（赛时内容不应被外部检索到）。
 */
export function applyDiscussSeo(discuss: DiscussView): void {
  const text = plainTextFromMarkdown(discuss.content, 110);
  const description = composeDescription([discuss.title, text]);
  const path = discussPath(discuss);
  const isContestDiscuss = discuss.contestId > 0;

  const jsonLd: Record<string, unknown>[] = isContestDiscuss
    ? []
    : [
        {
          "@context": "https://schema.org",
          "@type": "DiscussionForumPosting",
          headline: discuss.title,
          text: plainTextFromMarkdown(discuss.content, 400),
          url: toAbsoluteUrl(path),
          inLanguage: "zh-CN",
          ...(discuss.inserterNickname || discuss.inserterUsername
            ? {
                author: {
                  "@type": "Person",
                  name: discuss.inserterNickname || discuss.inserterUsername,
                },
              }
            : {}),
          ...(discuss.problemTitle
            ? {
                about: {
                  "@type": "LearningResource",
                  name: discuss.problemTitle,
                  url: toAbsoluteUrl(problemPath(discuss.problemKey)),
                },
              }
            : {}),
        },
      ];

  applySeo({
    description,
    canonical: path,
    type: "article",
    noindex: isContestDiscuss,
    jsonLd,
  });
}
